import { useState } from 'react';
import { Accordion, Alert, Badge, Group, Loader, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { collection, query, where } from 'firebase/firestore';

import { Fund } from '../../types';
import { useFirestore } from '../../helpers/firebaseContext';
import useFirestoreCollection from '../../helpers/useFirestoreCollection';
import { isNavStale, navDateOf } from './etf_common';
import EtfFund from './etf_fund';

//collapsed sections sit flat on the page background; only the open ones read as cards
const flat = { background: 'transparent', border: 0, boxShadow: 'none' };
const card = { background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', boxShadow: 'var(--mantine-shadow-sm)' };

//one expandable section per ETF, first one open. Collapsed sections pause their listeners (Mantine 9 Activity).
const EtfPage = () => {
    const db = useFirestore();
    const fundsQuery = query(collection(db, 'funds'), where('active', '==', true), where('type', '==', 'etf'));
    const { loading, data } = useFirestoreCollection(fundsQuery, true);
    const funds = (data ?? []) as Fund[];
    const [open, setOpen] = useState<string[] | null>(null);
    const opened = open ?? funds.slice(0, 1).map((f) => f.id);

    if (loading || !data) return <Loader />;
    if (funds.length === 0) {
        return (
            <Alert color="yellow" icon={<IconAlertTriangle />} title="No ETF fund">
                No active fund with type "etf" was found. Set <code>type: "etf"</code> on the fund document to manage it here.
            </Alert>
        );
    }

    return (
        <>
            <Title order={2} mb="md">ETFs</Title>
            <Accordion multiple value={opened} onChange={setOpen} variant="separated">
                {funds.map((fund) => {
                    const stale = isNavStale(fund);
                    return (
                        <Accordion.Item key={fund.id} value={fund.id} style={opened.includes(fund.id) ? card : flat}>
                            <Accordion.Control>
                                <Group justify="space-between" pr="md">
                                    <div>
                                        <Text fw={700} size="lg">{fund.name_en ?? 'ETF'}</Text>
                                        <Text size="sm" c="dimmed">
                                            {fund.id}{fund.pcf?.ticker ? ` · ${fund.pcf.ticker}` : ''} · NAV {fund.nav_per_unit != null ? fund.nav_per_unit.toFixed(4) : '—'} ({navDateOf(fund) ?? 'no date'})
                                        </Text>
                                    </div>
                                    <Badge color={stale ? 'red' : 'teal'} variant="filled">{stale ? 'NAV STALE' : 'NAV FRESH'}</Badge>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <EtfFund fund={fund} />
                            </Accordion.Panel>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </>
    );
};

export default EtfPage;
